import { Component, inject, viewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { NbDialogService } from '@nebular/theme';
import {
  RepairTaskDetailDto,
  RepairTaskFilterDto,
  RepairTaskItemDto,
  RepairTaskService,
  TenantItemDto,
  TenantService,
} from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { currentTenantId } from '@/utils/admin-utils';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { DataTableComponent } from '@shared/generic/data-table/data-table.component';
import {
  ContractorRepairTaskDetailDialogComponent,
} from './contractor-repair-task-detail-dialog/contractor-repair-task-detail-dialog.component';
import StatusEnum = RepairTaskDetailDto.StatusEnum;

const OPEN_STATUSES = 'OPEN';
type StatusFilter = StatusEnum | typeof OPEN_STATUSES | null;
type OverviewFilter = RepairTaskFilterDto & { tenant?: TenantItemDto | null; statusFilter?: StatusFilter };

@Component({
  standalone: false,
  selector: 'wr-contractor-repair-task-overview-page',
  templateUrl: './contractor-repair-task-overview-page.component.html',
})
export class ContractorRepairTaskOverviewPageComponent {
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly tenantService = inject(TenantService);
  private readonly dialogService = inject(NbDialogService);
  private readonly formBuilder = inject(FormBuilder);

  private readonly dataTable = viewChild(DataTableComponent);

  readonly currentTenantId = currentTenantId();
  readonly openStatuses = OPEN_STATUSES;
  readonly statusOptions = Object.values(StatusEnum).filter(status => status !== StatusEnum.Draft);

  readonly externalTenants = asyncComputed(() => {
    const tenantId = this.currentTenantId();
    if (tenantId) {
      return this.tenantService.findExternalTenantsByContractorId(tenantId);
    }
    return [];
  }, []);

  readonly form = this.formBuilder.group({
    text: this.formBuilder.control(''),
    statusFilter: this.formBuilder.control<StatusFilter>(OPEN_STATUSES),
    tenant: this.formBuilder.control<TenantItemDto | null>(null),
    tenantId: this.formBuilder.control<string | null>(null),
  });

  readonly loadRepairTasks = (filter: OverviewFilter) => this.repairTaskService.findAll(this.toApiFilter(filter));
  readonly getCount = (filter: OverviewFilter) => this.repairTaskService.getCount(this.toApiFilter(filter));

  constructor() {
    this.form.controls.tenant.valueChanges.subscribe(tenant => {
      this.form.controls.tenantId.setValue(tenant?.id ?? null);
    });
  }

  async openDetail(item: RepairTaskItemDto): Promise<void> {
    const ref = this.dialogService.open(ContractorRepairTaskDetailDialogComponent, {
      context: { repairTaskId: item.id },
    });
    const changed = await firstValueFrom(ref.onClose);
    if (changed) {
      this.dataTable()?.refresh$.next();
    }
  }

  private toApiFilter(filter: OverviewFilter): RepairTaskFilterDto {
    const { statusFilter, ...rest }: OverviewFilter = { ...filter };
    delete rest.tenant;
    if (statusFilter === OPEN_STATUSES) {
      return { ...rest, openOnly: true };
    }
    return { ...rest, status: statusFilter ?? undefined };
  }
}
